import clsx from "clsx";
import type { PlayerContract } from "../../../../common/types.ts";
import { helpers } from "../util/helpers.ts";
import i18n from "../util/i18n.ts";
import { useLocal, useLocalPartial } from "../util/local.ts";
import { MobileTooltip } from "./MobileTooltip.tsx";

type ContractPlayer = {
	draft: {
		year: number;
	};
	contract: PlayerContract;
};

const useJustDrafted = (p: ContractPlayer) => {
	const { phase, season } = useLocalPartial(["phase", "season"]);

	return helpers.justDrafted(p, phase, season);
};

const NON_GUARANTEED_CONTRACT_TEXT =
	"Contracts for drafted players are not guaranteed until the regular season. If you release a drafted player before then, you pay nothing.";

export const ContractAmount = ({
	p,
	override,
}: {
	p: ContractPlayer;
	override?: number;
}) => {
	const justDrafted = useJustDrafted(p);

	return (
		<MobileTooltip
			title={justDrafted ? NON_GUARANTEED_CONTRACT_TEXT : undefined}
		>
			<span className={justDrafted ? "fst-italic" : undefined}>
				{helpers.formatCurrency(override ?? p.contract.amount, "M")}
			</span>
		</MobileTooltip>
	);
};

export const wrappedContractAmount = (p: ContractPlayer, override?: number) => {
	const formatted = helpers.formatCurrency(override ?? p.contract.amount, "M");

	return {
		value: <ContractAmount p={p} override={override} />,
		sortValue: p.contract.amount,
		searchValue: formatted,
	};
};

export const ContractExp = ({
	p,
	override,
}: {
	p: ContractPlayer;
	override?: number;
}) => {
	const justDrafted = useJustDrafted(p);

	const season = useLocal((state) => state.season);
	const expiring = season === p.contract.exp;

	const title = justDrafted
		? NON_GUARANTEED_CONTRACT_TEXT
		: expiring
			? "Expiring contract"
			: undefined;

	return (
		<MobileTooltip title={title}>
			<span
				className={clsx({
					"fst-italic": justDrafted,
					"fw-bold": expiring,
				})}
			>
				{override ?? p.contract.exp}
			</span>
		</MobileTooltip>
	);
};

export const wrappedContractExp = (p: ContractPlayer, override?: number) => {
	const formatted = override ?? p.contract.exp;

	return {
		value: <ContractExp p={p} override={override} />,
		sortValue: formatted,
		searchValue: formatted,
	};
};

export const Contract = ({ p }: { p: ContractPlayer }) => {
	const justDrafted = useJustDrafted(p);

	return (
		<>
			<ContractAmount p={p} />
			<MobileTooltip
				title={justDrafted ? NON_GUARANTEED_CONTRACT_TEXT : undefined}
			>
				<span className={justDrafted ? "fst-italic" : undefined}>
					{" "}
					{i18n.t("playerProfile.thru", { ns: "ui", defaultValue: "thru" })}{" "}
				</span>
			</MobileTooltip>
			<ContractExp p={p} />
		</>
	);
};

export const wrappedContract = (p: ContractPlayer) => {
	const formattedAmount = helpers.formatCurrency(p.contract.amount, "M");
	const formatted = `${formattedAmount} thru ${p.contract.exp}`;

	return {
		value: <Contract p={p} />,
		sortValue: p.contract.amount,
		searchValue: formatted,
	};
};
